import { useCallback, useEffect, useState } from "react"
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react"
import { ArrowDownTrayIcon, ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from "@heroicons/react/20/solid"
import { EnkIWPServerPhotos, TEnkPhotoInfo } from "../../api/api-iwp-server"
import { getBackendUrl } from "../../api/do-request"

/** Bouw directe URL voor <img src> (niet via fetch, browser cached dit) */
function getImageUrl(bijlageInhoudId: string): string {
  return getBackendUrl() + EnkIWPServerPhotos.API_PATH + `/image?id=${encodeURIComponent(bijlageInhoudId)}`
}

/** Bouw directe URL voor zip download */
function getDownloadAllUrl(table: string, keyValue: string): string {
  return getBackendUrl() + EnkIWPServerPhotos.API_PATH + `/downloadAll?table=${encodeURIComponent(table)}&keyValue=${encodeURIComponent(keyValue)}`
}
import EnkLoader from "../common/loader"

interface PhotoGalleryProps {
  open: boolean
  onClose: () => void
  table: string
  keyValue: string
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function PhotoGallery({ open, onClose, table, keyValue }: PhotoGalleryProps) {
  const [photos, setPhotos] = useState<TEnkPhotoInfo[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)

  useEffect(() => {
    if (!open) return
    setLoading(true)
    setError(null)
    setCurrentIndex(0)
    EnkIWPServerPhotos.getList(table, keyValue)
      .then(setPhotos)
      .catch((err) => {
        console.error("Failed to load photos:", err)
        setError("Fout bij het ophalen van foto's.")
      })
      .finally(() => setLoading(false))
  }, [open, table, keyValue])

  const goPrev = useCallback(() => {
    setCurrentIndex((i) => (i > 0 ? i - 1 : photos.length - 1))
  }, [photos.length])

  const goNext = useCallback(() => {
    setCurrentIndex((i) => (i < photos.length - 1 ? i + 1 : 0))
  }, [photos.length])

  useEffect(() => {
    if (!open || photos.length <= 1) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") goPrev()
      if (e.key === "ArrowRight") goNext()
    }
    window.addEventListener("keydown", handleKey)
    return () => window.removeEventListener("keydown", handleKey)
  }, [open, photos.length, goPrev, goNext])

  const handleDownloadAll = useCallback(async () => {
    setDownloading(true)
    try {
      const url = getDownloadAllUrl(table, keyValue)
      const resp = await fetch(url, { credentials: "include" })
      if (!resp.ok) throw new Error("Download mislukt")
      const blob = await resp.blob()
      const a = document.createElement("a")
      a.href = URL.createObjectURL(blob)
      a.download = `Fotos_${keyValue}.zip`
      a.click()
      URL.revokeObjectURL(a.href)
    } catch (err) {
      console.error("Download failed:", err)
    } finally {
      setDownloading(false)
    }
  }, [table, keyValue])

  const current = photos[currentIndex]

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/70" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="relative flex flex-col items-center bg-white rounded-lg shadow-xl max-h-[90vh] max-w-[90vw]">
          {/* Toolbar */}
          <div className="absolute top-2 right-2 z-10 flex gap-1">
            {photos.length > 0 && (
              <button
                onClick={handleDownloadAll}
                disabled={downloading}
                title="Download alle foto's als zip"
                className="rounded-full bg-white/80 p-1 text-gray-600 hover:bg-white hover:text-gray-900 disabled:opacity-50"
              >
                <ArrowDownTrayIcon className="h-5 w-5" />
              </button>
            )}
            <button
              onClick={onClose}
              className="rounded-full bg-white/80 p-1 text-gray-600 hover:bg-white hover:text-gray-900"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {loading && (
            <div className="flex items-center justify-center p-12">
              <EnkLoader />
            </div>
          )}

          {error && (
            <div className="p-8 text-red-500 text-sm">{error}</div>
          )}

          {!loading && !error && photos.length === 0 && (
            <div className="p-8 text-gray-500 text-sm">Geen foto's gevonden.</div>
          )}

          {!loading && !error && current && (
            <>
              {/* Foto */}
              <div className="flex items-center justify-center p-4 min-h-[300px]">
                {photos.length > 1 && (
                  <button
                    onClick={goPrev}
                    className="shrink-0 rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                  >
                    <ChevronLeftIcon className="h-8 w-8" />
                  </button>
                )}
                <img
                  src={getImageUrl(current.bijlageInhoudId ?? "")}
                  alt={current.omschrijving || "Foto"}
                  className="max-h-[70vh] max-w-[70vw] object-contain rounded"
                />
                {photos.length > 1 && (
                  <button
                    onClick={goNext}
                    className="shrink-0 rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                  >
                    <ChevronRightIcon className="h-8 w-8" />
                  </button>
                )}
              </div>

              {/* Footer */}
              <div className="w-full border-t border-gray-200 px-4 py-2 text-center text-sm text-gray-600">
                <span>Foto {currentIndex + 1} van {photos.length}</span>
                {current.omschrijving && (
                  <span className="ml-3">{current.omschrijving}</span>
                )}
                {current.grootte > 0 && (
                  <span className="ml-3 text-gray-400">{formatFileSize(current.grootte)}</span>
                )}
              </div>
            </>
          )}
        </DialogPanel>
      </div>
    </Dialog>
  )
}
